import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Loader2, Lock, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO_URL } from "../lib/constants";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`¡Hola ${res.user.name || ""}!`, {
        description: "Sesión iniciada correctamente.",
      });
      const dest =
        res.user?.role === "admin"
          ? "/admin"
          : location.state?.from?.pathname || "/perfil";
      nav(dest, { replace: true });
    } else {
      toast.error("No se pudo iniciar sesión", { description: res.error });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5 py-10 relative">
      <div className="absolute inset-0 bg-blueprint opacity-50 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link
          to="/"
          data-testid="back-to-site"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-pv-green mb-6 transition-colors"
        >
          <ArrowLeft size={16} /> Volver al sitio
        </Link>
        <div className="bg-white border border-slate-200 rounded-sm p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="Pino Verde" className="h-10 w-10" />
            <div>
              <div className="font-display font-extrabold text-pv-green tracking-tight">
                PINO VERDE
              </div>
              <div className="overline text-pv-orange -mt-0.5">Iniciar sesión</div>
            </div>
          </div>

          <h1 className="font-display font-bold text-pv-green text-2xl mb-1">
            Bienvenido de nuevo
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Ingrese con su correo y contraseña.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">
                Correo
              </Label>
              <div className="relative mt-1.5">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="su@correo.com"
                  className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">
                Contraseña
              </Label>
              <div className="relative mt-1.5">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 pr-10 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-pv-green transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 rounded-sm bg-pv-green hover:bg-pv-green/90 text-white font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Ingresando...
                </>
              ) : (
                "Ingresar"
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 text-center text-sm text-slate-600">
            ¿No tenés cuenta?{" "}
            <Link
              to="/registro"
              data-testid="go-to-register"
              className="text-pv-orange font-semibold hover:underline"
            >
              Crear cuenta
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
